import { useAppStore } from "@/hooks/useAppStore";
import { ClientConfig } from "../../shared/types";

/**
 * Calculates the FNV-1a hash of a string. Non-cryptographic.
 * Used for generating consistent but varied outputs based on input UUIDs.
 * @param str The input string (typically a UUID).
 * @returns An unsigned 32-bit integer hash. Returns 0 if input is null/undefined.
 */
export function fnv1aHash(str?: string | null): number {
  if (!str) return 0;
  let hash = 0x811c9dc5; // FNV offset basis

  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    // Equivalent to hash * FNV_prime (0x01000193) using shifts for performance
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }

  return hash >>> 0; // Ensure unsigned 32-bit integer
}

const adjectives = [
  "Pink", "Blue", "Gray", "Black", "White", "Red", "Yellow",
  "Cute", "Sweet", "Fearless", "Gentle", "Dreaming", "Bouncy", 
  "Happy", "Kind", "Young", "Smart", "Wild", "Lucky", "Playful", "Quiet",
  "Funny", "Tiny", "Brave", "Calm", "Agile", "Cool", "Hot", "Cold", "Warming",
  "Dark", "Light", "Bright", "Fresh", "Sharp", "Clean", "Strong", "Simple",
  "Special", "Natural", "Magic", "Spicy", "Lovely", "Classic", "Modern",
  "Stylish", "Proud", "Quick", "Joyful", "Sparkling", "Cozy", 
  "Electric", "Golden", "Shiny", "Tasty", "Soft", "Hard", "Charming", "Fancy",
  "Elegant", "Loyal", "Mighty", "Unique", "Groovy", "Fluffy", "Jazzy", "Tough",
];

const cuteAnimals = [
  "Cat", "Bunny", "Dog", "Puppy", "Bear", "Duck", "Horse", "Mouse", "Elephant", "Lion", "Tiger", "Sheep",
  "Lamb", "Goat", "Deer", "Monkey", "Fox", "Wolf", "Penguin", "Frog", "Chicken", "Turtle", "Dolphin",
  "Owl", "Panda", "Squirrel", "Raccoon", "Hedgehog", "Bat", "Crab", "Seal", "Camel", "Giraffe",
  "Flamingo", "Shark", "Butterfly", "Caterpillar", "Bee", "Crow", "Pigeon", "Hamster", "Crab", "Snake",
  "Capybara", "Shiba", "Corgi", "Tuna", "Tanuki", "Bird", "Fish", "Pig", "Rabbit", "Zebra", "Kangaroo",
  "Koala", "Neko"
];

const fruits = [
  "Apple", "Peach", "Strawberry", "Yuzu", "Ume", "Melon", "Grape", "Mikan", "Banana", "Suica",
  "Watermelon", "Kiwi", "Lychee", "Lemon", "Blueberry",  "Pineapple", "Raspberry", "Coconut",
  "Cherry",  "Kaki", "Sudachi", "Ginger", "Wasabi", "Potato", "Edamame",
  "Corn", "Lotus", "Pumpkin", "Carrot", "Bamboo", "Shiso", "Kinoko", "Nasu", "Mango",
  "Tomato", "Onion", "Cucumber", "Lettuce", "Broccoli", "Celery"
];

const misc = [
  "Coffee", "Cheese", "Butter", "Juice", "Chocolate", "Cake", "Sakura", "Sushi", "Onigiri",
  "Samurai", "Ninja"
];

// Combine nouns for more variety
const nouns = [...cuteAnimals, ...fruits, ...misc];

/**
 * Generates a consistent, human-readable username (e.g., "AgileShiba") from a UUID.
 * Special case: Maps '_chottochan' UUID to 'ChottoChan'.
 * @param uuid The input UUID string.
 * @returns A generated username string.
 */
export function createConsistentUsernameFromUUID(uuid: string): string {
  // Handle special case for the AI assistant
  if (uuid === '_chottochan') {
    return 'ChottoChan';
  }

  // Use the FNV-1a hash for pseudo-random index generation
  const hash = fnv1aHash(uuid);

  // Select words based on hash modulo the array lengths
  // Using large prime numbers helps distribute results more evenly across combinations
  const adjective = adjectives[hash % adjectives.length];
  const noun = nouns[Math.floor(hash / 101) % nouns.length]; // Divide hash before modulo
  // const color = colors[Math.floor(hash / 101 / 101) % colors.length];

  return `g-${adjective}${noun}`;
}

/**
 * Calculates a simple hash code for a string (djb2 variation).
 * Used specifically for color generation to get a different distribution than FNV-1a.
 * @param str The input string (typically a UUID).
 * @returns A hash code number. Returns 0 if input is null/undefined.
 */
export function hashUUID(str?: string | null): number {
  if (!str) return 0;
  let hash = 5381; // Initial seed
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i); // hash * 33 + charCode (using XOR variation)
  }
  return Math.abs(hash); // Return absolute value
}
const bubbleColors = [
  "#baf4fa",
  "#95f7f8",
  "#aed4de",
  "#b0dbf6",
];

/**
 * Generates a consistent HSL color string suitable for dark mode backgrounds,
 * based on a UUID.
 * @param uuid The input UUID string.
 * @returns An HSL color string (e.g., "hsl(123, 75%, 35%)").
 */
function getUserBubbleColor(uuid: string, palette?: string[]): string {
  const hash = hashUUID(uuid); // Use the specific hash function for colors
  const colorsList = palette || bubbleColors;
  return colorsList[hash % bubbleColors.length];
  /**
  // Generate HSL values within specific ranges for good contrast on dark backgrounds
  const hue = hash % 360;                    // Full color wheel (0-359)
  const saturation = 60 + (hash % 21);       // Saturation: 60% – 80%
  const lightness = 30 + (hash % 11);        // Lightness: 30% – 40%

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
   */
}

/**
 * Returns a specific color for the current user ("isMe") and generates
 * a unique color for other users based on their username (UUID).
 * @param username The username (UUID) of the message sender.
 * @param currentUsername The username (UUID) of the currently logged-in user.
 * @returns An HSL color string.
 */
export function getUserColor(username: string, currentUsername: string, clientConfig: ClientConfig | null): string {
  // const { clientConfig } = useAppStore();
  if (username === currentUsername) {
    // Return a fixed, distinct color for the current user's messages
    return clientConfig?.theme?.light?.primaryMessageBackground || '#96e5f9';
  } else if (username === '_chottochan') {
    // Optional: Specific color for the AI assistant
    return `hsl(48, 89%, 50%)`; // Chotto Gold
  } else {
    // Generate a color based on the other user's UUID
    return getUserBubbleColor(username, clientConfig?.theme?.light?.chatBubbleColors);
  }
}

/**
 * Darkens a given HSL color string by reducing its lightness component.
 * @param hslStr The input HSL color string (e.g., "hsl(H, S%, L%)").
 * @param amount The percentage amount to reduce lightness by (default: 10).
 * @returns A new HSL color string with reduced lightness, clamped between 0% and 100%.
 * @throws Error if the input string is not a valid HSL format.
 */
export function darkenHSL(hslStr: string, amount = 10): string {
  // Regular expression to extract H, S, L values from "hsl(H, S%, L%)" format
  const hslMatch = hslStr.match(/^hsl\(\s*([\d.]+),\s*([\d.]+)%?,\s*([\d.]+)%?\s*\)$/);

  if (!hslMatch) {
    console.error("Invalid HSL format provided to darkenHSL:", hslStr);
    // Return the original string or a default dark color as fallback
    return hslStr; // Or return 'hsl(0, 0%, 10%)' for a very dark gray
  }

  // Extract values - Array destructuring with type assertion (less safe) or index access
  // Using index access for clarity here
  const h = parseFloat(hslMatch[1]);
  const s = parseFloat(hslMatch[2]);
  let l = parseFloat(hslMatch[3]);

  // Reduce lightness, ensuring it stays within the valid 0-100 range
  l = Math.max(0, Math.min(100, l - amount));

  // Return the new HSL string
  return `hsl(${h}, ${s}%, ${l}%)`;
}

/**
 * Checks if the username has chance of overlapping with the gnerated UUIDs.
 */
export function isAllowedUsername(username: string) {
  username = username?.toLowerCase()?.trim();

  //reserved usernames
  if (!username || username === "chottochan" || username === "_chottochan") {
    return false;
  }

  const matchingAdjective = adjectives.find(adj =>
    username.startsWith(adj.toLowerCase())
  );

  if (matchingAdjective) {
    const remaining = username.slice(matchingAdjective.length);
    const nounMatch = nouns.find(noun => remaining === noun.toLowerCase());
    if (nounMatch) {
      return false;
    }
  }

  return true;
}
